<template>
  <div ref="root">
    <table
      class="entsys--table fipa-app-table"
      :class="tableClass"
      :style="tableStyle"
    >
      <slot name="colgroup" />
      <thead>
        <slot v-if="!hideHeaders" name="headers">
          <tr>
            <th
              v-for="header in headers"
              :key="header.text || header.title"
              :class="header.class || 'text-start'"
              :style="header.style"
            >
              {{ header.text || header.title }}
            </th>
          </tr>
        </slot>
      </thead>
      <tbody>
        <template v-if="loading">
          <tr>
            <td colspan="99" class="pa-0">
              <div>
                <v-progress-linear indeterminate />
              </div>
            </td>
          </tr>
          <template v-if="!items.length">
            <tr>
              <td colspan="99" class="muted--text text-center">
                {{
                  $t("common:loading.messsage", "Loading...", "Loading message")
                }}
              </td>
            </tr>
          </template>
        </template>
        <tr v-if="!hideNoData && !loading && items.length === 0">
          <td colspan="999" class="text-center muted--text">
            <slot name="no-data">{{
              $t("common:NoResults.messsage", "No data available")
            }}</slot>
          </td>
        </tr>

        <slot name="items" v-bind="{ toggleItemExpand, itemIsExpanded }" />
      </tbody>
    </table>
  </div>
</template>
<script>
export default {
  name: "AppTable"
};
</script>
<script setup>
import {
  ref,
  computed,
  watch,
  onUpdated,
  onBeforeUpdate,
  onMounted
} from "vue";
const props = defineProps({
  loading: {
    type: Boolean,
    default: false
  },
  headers: {
    type: Array,
    default: () => []
  },
  items: {
    type: Array,
    default: () => []
  },
  tableClass: {
    type: String,
    default: undefined
  },
  rowHeightMargin: {
    type: Number,
    default: 0.35
  },
  hideNoData: {
    type: Boolean,
    default: false
  },
  tableStyle: {
    type: [String, Object],
    default: undefined
  },
  expanded: {
    type: Array,
    default: null
  },
  hideHeaders: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["update:expanded"]);

const root = ref();

const localExpanded = ref(props.expanded || []);
function itemIsExpanded(id) {
  return expandedHandler.value.includes(id);
}
function toggleItemExpand(id) {
  if (expandedHandler.value.includes(id)) {
    expandedHandler.value = localExpanded.value.filter(x => x != id);
  } else expandedHandler.value = [...localExpanded.value, id];
}

const expandedHandler = computed({
  get() {
    return localExpanded.value;
  },
  set(v) {
    localExpanded.value = v;
    emit("update:expanded", v);
  }
});

watch(
  () => props.expanded,
  v => {
    if (JSON.stringify(v) != JSON.stringify(localExpanded.value)) {
      localExpanded.value = v;
    }
  }
);

// Dynamically set fixed height to vertical dividers
function setupDividersFixedHeight() {
  function looper(row) {
    const rowHeight = row.getBoundingClientRect().height;
    if (!row.querySelector(".cell-vertical-divider") || rowHeight == 0) return;
    const dividerHeight = rowHeight - rowHeight * props.rowHeightMargin;
    row.querySelectorAll(".cell-vertical-divider > div").forEach(divider => {
      divider.style.height = dividerHeight + "px";
    });
  }

  if (!root.value) return;

  root.value.querySelectorAll("tr").forEach(looper);
}

onMounted(() => {
  setupDividersFixedHeight();
});

// Reset it
onBeforeUpdate(() => {
  root.value.querySelectorAll(".cell-vertical-divider > div").forEach(x => {
    x.style.height = "";
  });
});

onUpdated(() => {
  setupDividersFixedHeight();
});
</script>
<style lang="scss">
.entsys--table.fipa-app-table {
  tr th {
    text-align: left;
  }
  @media (max-width: 576px) {
    font-size: 12px;
  }
}

.fipa-app-table {
  --v-fipa-border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  --v-fipa-border-color: rgba(0, 0, 0, 0.12);

  tbody {
    @media (max-width: 576px) {
      tr,
      tr.primary-light {
        border-bottom: var(--v-fipa-border-bottom) !important;
        border-color: var(--v-fipa-border-color) !important;
      }
    }
  }

  @media (max-width: 768px) {
    td.mobile-padding-right {
      padding-right: 20px !important;
    }
    td.mobile-padding-left {
      padding-left: 20px !important;
    }
  }
}
</style>
